import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Icon } from "@bedrock/ui";
import { NOTE_PIN_MAX_LENGTH, NOTE_PIN_MIN_LENGTH, notePinProblem } from "./note-lock";

export type NoteLockDialogMode = "lock" | "unlock" | "remove";

// Unlocking is tried quietly once typing pauses, so the item opens on the last correct digit.
const QUIET_UNLOCK_DELAY_MS = 350;

/**
 * Compact PIN sheet for locking, opening, or unlocking a note. `onSubmit` resolves to an error
 * message to show, or null once the sheet's work is done. `itemTitle` is null for notes.
 */
export function NoteLockDialog({ mode, itemTitle, folder = false, onSubmit, onCancel }: {
  mode: NoteLockDialogMode;
  itemTitle: string | null;
  /** Folders lock, open, and unlock everything inside them. */
  folder?: boolean;
  onSubmit: (pin: string) => Promise<string | null>;
  onCancel: () => void;
}) {
  const headingId = useId();
  const promptId = useId();
  const input = useRef<HTMLInputElement>(null);
  const dots = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const [pin, setPin] = useState("");
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const named = itemTitle ? `“${itemTitle}”` : null;
  const confirming = mode === "lock" && firstPin !== null;
  const heading = mode === "unlock" ? itemTitle ?? (folder ? "Locked folder" : "Locked note") : mode === "lock" ? named ? `Lock ${named}` : folder ? "Lock folder" : "Lock note" : "Remove lock";
  const prompt = mode === "unlock" ? "Enter the PIN"
    : mode === "remove" ? `Enter the PIN for ${named ?? (folder ? "this folder" : "this note")}`
      : confirming ? "Enter it again to confirm" : `Choose a PIN (${NOTE_PIN_MIN_LENGTH} or more digits)`;
  const hint = folder
    ? mode === "lock" ? "Everything inside is encrypted. Forgotten PINs can't be recovered." : mode === "remove" ? "Everything inside will be saved without encryption." : ""
    : mode === "lock" ? "Forgotten PINs can't be recovered." : mode === "remove" ? "It will be saved without encryption." : "";
  const busyLabel = folder && mode === "lock" ? "Encrypting…" : folder && mode === "remove" ? "Decrypting…" : "Checking…";
  const action = mode === "unlock" ? "Open" : mode === "remove" ? "Remove lock" : confirming ? "Lock" : "Next";

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    // The field is disabled while a PIN is checked and can't take focus until it is enabled again.
    if (!busy) input.current?.focus();
  }, [busy]);

  useEffect(() => {
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      cancel();
    };
    window.addEventListener("keydown", cancelOnEscape, { capture: true });
    return () => window.removeEventListener("keydown", cancelOnEscape, { capture: true });
  });

  useEffect(() => {
    if (mode !== "unlock" || busy || pin.length < NOTE_PIN_MIN_LENGTH) return;
    // A wrong quiet guess stays silent; errors only appear when the user asks to open.
    const timer = window.setTimeout(() => void onSubmit(pin).catch(() => null), QUIET_UNLOCK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [busy, mode, onSubmit, pin]);

  const reject = (message: string) => {
    setError(message);
    setPin("");
    const element = dots.current;
    if (element && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.classList.remove("is-shaking");
      void element.offsetWidth;
      element.classList.add("is-shaking");
    }
    input.current?.focus();
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    if (mode === "lock") {
      const problem = notePinProblem(pin);
      if (problem) return reject(problem);
      if (firstPin === null) {
        setFirstPin(pin);
        setPin("");
        setError(null);
        return;
      }
      if (pin !== firstPin) {
        setFirstPin(null);
        return reject("They didn't match. Start again.");
      }
    } else if (!pin) {
      return reject("Enter the PIN.");
    }
    setBusy(true);
    setError(null);
    let failure: string | null;
    try {
      failure = await onSubmit(pin);
    } catch {
      failure = "Something went wrong. Try again.";
    }
    if (failure === null || !mounted.current) return;
    setBusy(false);
    reject(failure);
  };

  function cancel() {
    if (!busy) onCancel();
  }

  const slots = Math.max(NOTE_PIN_MIN_LENGTH, pin.length);
  return <div className="note-lock-layer" role="presentation" onMouseDown={cancel}>
    <form
      className="note-lock-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-busy={busy || undefined}
      onMouseDown={(event) => event.stopPropagation()}
      onSubmit={(event) => void submit(event)}
      noValidate
    >
      <span className="note-lock-sheet__icon" aria-hidden="true"><Icon name={mode === "remove" ? "unlock" : "lock"}/></span>
      <strong id={headingId}>{heading}</strong>
      <label id={promptId} htmlFor="note-lock-pin">{prompt}</label>
      <div className="note-lock-sheet__pin" onClick={() => input.current?.focus()}>
        <input
          ref={input}
          id="note-lock-pin"
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          autoFocus
          maxLength={NOTE_PIN_MAX_LENGTH}
          value={pin}
          disabled={busy}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "note-lock-error" : undefined}
          onChange={(event) => { setPin(event.target.value.replace(/\D/g, "")); if (error) setError(null); }}
        />
        <div ref={dots} className="note-lock-sheet__dots" aria-hidden="true" onAnimationEnd={(event) => event.currentTarget.classList.remove("is-shaking")}>
          {Array.from({ length: slots }, (_, index) => <span key={index} className={index < pin.length ? "is-filled" : undefined}/>)}
        </div>
      </div>
      {error
        ? <p id="note-lock-error" className="note-lock-sheet__hint is-error" role="alert">{error}</p>
        : <p className="note-lock-sheet__hint">{busy ? busyLabel : hint}</p>}
      <footer>
        <button type="button" onClick={cancel} disabled={busy}>Cancel</button>
        <button type="submit" className="is-primary" disabled={busy}>{action}</button>
      </footer>
    </form>
  </div>;
}
